/* ============================================================
 * Bundle PDP JS
 *   - <bundle-purchase-options> custom element: Subscribe vs One-time
 *     card toggle, frequency dropdown, selling_plan + header price sync.
 *   - Lightweight delegated handlers for the info side-drawer used by
 *     the bundle_trust_links block (Risk Free / Shipping Details).
 * ============================================================ */

(function () {
  /* ----------------------------- 1. <bundle-purchase-options> ----------------------------- */
  if (!customElements.get('bundle-purchase-options')) {
    class BundlePurchaseOptions extends HTMLElement {
      connectedCallback() {
        this.cards = this.querySelectorAll('[data-bundle-purchase-card]');
        this.radios = this.querySelectorAll('input[type="radio"][name="bundle_purchase_option"]');
        this.sellingPlanInput = this.querySelector('.selected-selling-plan-id');
        this.promoOfferInput = this.querySelector('.bundle-promo-offer-input');
        this.frequencyOptions = this.querySelectorAll('[data-bundle-frequency-option]');

        if (!this.sellingPlanInput || this.radios.length === 0) return;

        this.appendSellingPlanInputs();

        this.radios.forEach((radio) =>
          radio.addEventListener('change', () => this.handleRadioChange()),
        );

        this.frequencyOptions.forEach((btn) => {
          btn.addEventListener('click', (e) => {
            // The buttons sit inside a <label>; stop the click from toggling
            // the subscribe radio.
            e.preventDefault();
            e.stopPropagation();
            this.handleFrequencyChange(btn);
          });
        });

        this.bindTooltips();

        // Initial sync to whichever radio is checked.
        this.handleRadioChange();
      }

      /*
        Perks live inside <label>, so a tap on the (i) button would normally
        bubble up and toggle the radio. We stop propagation and add a tap-to-stick
        behaviour for mobile via an `.is-open` class.
      */
      bindTooltips() {
        const triggers = this.querySelectorAll('[data-bundle-tooltip-trigger]');
        triggers.forEach((btn) => {
          btn.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            const wrap = btn.closest('[data-bundle-tooltip]');
            if (!wrap) return;
            const wasOpen = wrap.classList.contains('is-open');
            // Close any other open tooltips inside this component.
            this.closeTooltips();
            if (!wasOpen) this.setTooltipOpen(wrap, true);
          });
        });

        // Click anywhere outside a tooltip closes any sticky-open ones.
        document.addEventListener('click', (e) => {
          if (e.target.closest('[data-bundle-tooltip]')) return;
          this.closeTooltips();
        });
      }

      /*
        Keep the trigger's aria-expanded in step with the tap-to-stick state, so
        assistive tech is told when the (aria-describedby-linked) tooltip is shown.
      */
      setTooltipOpen(wrap, isOpen) {
        wrap.classList.toggle('is-open', isOpen);
        const trigger = wrap.querySelector('[data-bundle-tooltip-trigger]');
        if (trigger) trigger.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
      }

      closeTooltips() {
        for (const wrap of this.querySelectorAll('[data-bundle-tooltip].is-open')) {
          this.setTooltipOpen(wrap, false);
        }
      }

      get rootElement() {
        return this.closest('product-info');
      }

      get addToCartForms() {
        if (!this.rootElement) return [];
        return this.rootElement.querySelectorAll('form[action*="/cart/add"]');
      }

      get selectedRadio() {
        return Array.from(this.radios).find((r) => r.checked);
      }

      /*
        Mirror the legacy <selling-plans-widget>: clone the hidden selling_plan input
        into every add-to-cart form so the value submits with the cart line. The $99
        landing page also clones its _promo_offer property input alongside it.
      */
      appendSellingPlanInputs() {
        this.addToCartForms.forEach((form) => {
          if (!form.querySelector('input[name="selling_plan"]')) {
            form.appendChild(this.sellingPlanInput.cloneNode());
          }

          if (this.promoOfferInput && !form.querySelector('.bundle-promo-offer-input')) {
            form.appendChild(this.promoOfferInput.cloneNode());
          }
        });
      }

      get selectedFrequencyOption() {
        return Array.from(this.frequencyOptions).find((btn) =>
          btn.classList.contains('is-selected'),
        );
      }

      handleRadioChange() {
        const selected = this.selectedRadio;
        if (!selected) return;

        this.cards.forEach((card) => card.classList.toggle('is-active', card.contains(selected)));

        let sellingPlanId = '';
        if (selected.value === 'subscribe') {
          const activeFreqBtn = this.selectedFrequencyOption;
          if (activeFreqBtn) {
            sellingPlanId = activeFreqBtn.dataset.sellingPlanId || '';
          } else {
            sellingPlanId = selected.dataset.sellingPlanId || '';
          }
        }
        this.updateSellingPlanInputs(sellingPlanId);
        this.updateHeaderPrice(
          selected.dataset.variantPrice,
          selected.dataset.variantCompareAtPrice,
        );
        this.updateSaveBadge(selected.value === 'subscribe');
      }

      /*
        The mobile "SAVE X%" badges near the price describe the subscription saving,
        so hide them while One-time is selected: the ingredient_save block's badge
        (routine templates) and the title price badge (all other templates).
      */
      updateSaveBadge(isSubscribe) {
        const ingredientBadge = this.rootElement?.querySelector('.product-ingredient-save__badge');
        if (ingredientBadge) {
          ingredientBadge.classList.toggle('product-ingredient-save__badge--hidden', !isSubscribe);
        }

        const priceBadge = this.rootElement?.querySelector('.product__save-badge');
        if (priceBadge) priceBadge.classList.toggle('product__save-badge--hidden', !isSubscribe);
      }

      handleFrequencyChange(clickedBtn) {
        if (!clickedBtn) return;

        // Update the selected pill visual state + a11y.
        this.frequencyOptions.forEach((btn) => {
          const isSelected = btn === clickedBtn;
          btn.classList.toggle('is-selected', isSelected);
          btn.setAttribute('aria-checked', isSelected ? 'true' : 'false');
        });

        const sellingPlanId = clickedBtn.dataset.sellingPlanId || '';
        const variantPrice = clickedBtn.dataset.variantPrice;
        const variantCompareAtPrice = clickedBtn.dataset.variantCompareAtPrice;

        // Keep the SUBSCRIBE radio's data attributes in sync with the active
        // frequency so a fresh handleRadioChange() reads the right values.
        const subRadio = this.querySelector('input[value="subscribe"]');
        if (subRadio) {
          subRadio.dataset.sellingPlanId = sellingPlanId;
          if (variantPrice) subRadio.dataset.variantPrice = variantPrice;
          if (variantCompareAtPrice)
            subRadio.dataset.variantCompareAtPrice = variantCompareAtPrice;
        }

        // If the subscribe card is currently selected, propagate the change
        // to the hidden selling_plan input + header price. If it's not, just
        // remember the choice for when the user activates it.
        if (subRadio?.checked) {
          this.updateSellingPlanInputs(sellingPlanId);
          this.updateHeaderPrice(variantPrice, variantCompareAtPrice);
        }

        this.updateDeliveryPerk(clickedBtn);
      }

      /*
        Keep the default "Delivers every X days" perk in sync with the selected
        frequency pill. Only present when the perk text wasn't overridden by a
        product metafield or block setting (data attribute set in Liquid).
      */
      updateDeliveryPerk(freqBtn) {
        const perk = this.querySelector('[data-bundle-delivery-perk]');
        if (!perk || !freqBtn) return;
        const label = (freqBtn.textContent || '').trim().toLowerCase();
        if (!label) return;
        const template = perk.dataset.deliveryTemplate || 'Delivers every [FREQUENCY]';
        perk.textContent = template.replace('[FREQUENCY]', label);
      }

      updateSellingPlanInputs(value) {
        this.sellingPlanInput.value = value;
        this.addToCartForms.forEach((form) => {
          const input = form.querySelector('input[name="selling_plan"]');
          if (input) input.value = value;

          // The promo property is only sent with a subscription (selling plan set);
          // a disabled input is left out of the submitted form data.
          const promoInput = form.querySelector('.bundle-promo-offer-input');
          if (promoInput) promoInput.disabled = !value;
        });
      }

      /*
        Sync the header price block ('.price') to the active card.
        Mirrors the behaviour of the legacy <selling-plans-widget>.updatePrice().
      */
      updateHeaderPrice(price, compareAtPrice) {
        if (!price) return;

        // Let price displays outside this section (e.g. the landing page's
        // <sticky-offer-bar>) follow the active purchase option.
        document.dispatchEvent(
          new CustomEvent('bundle-purchase-options:price-change', {
            detail: { price, compareAtPrice },
          }),
        );

        if (!this.rootElement) return;
        const priceContainer = this.rootElement.querySelector('.price');
        if (!priceContainer) return;

        const regular = priceContainer.querySelector('.price__regular');
        const sale = priceContainer.querySelector('.price__sale');
        const saleValue = sale?.querySelector('.price-item--sale');
        const compareValue = sale?.querySelector('.price-item--regular');
        const regularValue = regular?.querySelector('.price-item--regular');

        const hasDiscount = compareAtPrice && compareAtPrice !== price;

        if (hasDiscount) {
          if (sale) {
            sale.style.display = 'block';
            if (saleValue) saleValue.innerHTML = price;
            if (compareValue) compareValue.innerHTML = compareAtPrice;
          }
          if (regular) regular.style.display = 'none';
          priceContainer.classList.add('price--on-sale');
        } else {
          if (regular) {
            regular.style.display = 'block';
            if (regularValue) regularValue.innerHTML = price;
          }
          if (sale) sale.style.setProperty('display', 'none', 'important');
          priceContainer.classList.remove('price--on-sale');
        }
      }
    }

    customElements.define('bundle-purchase-options', BundlePurchaseOptions);
  }

  /* --------------------------------- 2. Info side drawer --------------------------------- */
  // Mount drawers on <body> so fixed positioning stacks above the sticky header
  // (same reason the cart drawer lives outside the product section).
  function mountInfoDrawers() {
    document.querySelectorAll('[data-info-drawer]').forEach((drawer) => {
      if (drawer.parentElement !== document.body) {
        document.body.appendChild(drawer);
      }
    });
  }

  mountInfoDrawers();
  document.addEventListener('shopify:section:load', mountInfoDrawers);

  function openInfoDrawer(drawer) {
    if (!drawer) return;
    mountInfoDrawers();
    drawer.classList.add('is-open');
    drawer.setAttribute('aria-hidden', 'false');
    document.body.classList.add('info-drawer-open');
  }

  function closeInfoDrawer(drawer) {
    if (!drawer) return;
    drawer.classList.remove('is-open');
    drawer.setAttribute('aria-hidden', 'true');
    // Only remove the body lock if no other drawers are open.
    if (!document.querySelector('[data-info-drawer].is-open')) {
      document.body.classList.remove('info-drawer-open');
    }
  }

  document.addEventListener('click', (e) => {
    const opener = e.target.closest('[data-info-drawer-open]');
    if (opener) {
      e.preventDefault();
      const id = opener.getAttribute('data-info-drawer-open');
      openInfoDrawer(document.getElementById(id));
      return;
    }
    const closer = e.target.closest('[data-info-drawer-close]');
    if (closer) {
      closeInfoDrawer(closer.closest('[data-info-drawer]'));
    }
  });

  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape') return;
    document.querySelectorAll('[data-info-drawer].is-open').forEach((d) => closeInfoDrawer(d));
  });
})();
